import axios, { CancelTokenSource } from 'axios';
import { LeadFormData, BusinessData, TeamMember } from '../types';

// API base URL - use the environment variable or fallback to localhost
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://127.0.0.1:3000/api';

// API version control - use the environment variable or fallback to 'search'
let BUSINESS_SEARCH_API = import.meta.env.VITE_BUSINESS_SEARCH_API || 'search';

// Contact search API - use the environment variable or fallback to 'ai'
let CONTACT_SEARCH_API = import.meta.env.VITE_CONTACT_SEARCH_API || 'ai';

// Create custom events for API changes
const searchApiChangedEvent = new Event('searchApiChanged');
const contactApiChangedEvent = new Event('contactApiChanged');

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  },
  withCredentials: true
});

// Define response types with message and suggestions
interface ApiResponse {
  message?: string;
  suggestions?: string[];
  error?: string;
  id?: string;
  noMoreResults?: boolean;
  description?: string;
  email?: string;
  socialLinks?: string[];
  relevancyScore?: number;
}

type BusinessesResponse = BusinessData[] & ApiResponse;
type ContactsResponse = TeamMember[] & ApiResponse;
type BusinessInfoResponse = {
  business: BusinessData;
  contacts: TeamMember[];
  summary: string;
} & ApiResponse;

interface SupplementProfileRequest {
  description?: string;
  email?: string;
  relevancyScore?: number;
  contacts?: TeamMember[];
}

interface SupplementProfileResponse {
  newContacts: TeamMember[];
  updatedContacts: TeamMember[];
  summary: string;
  description?: string;
  email?: string;
  socialLinks?: string[];
  relevancyScore?: number;
  id?: string;
}

class ApiService {
  // Track active request tokens
  private activeTokens: CancelTokenSource[] = [];

  // Create a new cancel token and track it
  createCancelToken(): CancelTokenSource {
    const source = axios.CancelToken.source();
    this.activeTokens.push(source);
    return source;
  }

  // Cancel all active requests
  cancelAllRequests(): void {
    this.activeTokens.forEach(source => {
      try {
        source.cancel('Operation cancelled by user');
      } catch (error) {
        console.error('Error cancelling request:', error);
      }
    });
    // Clear the tokens array
    this.activeTokens = [];
  }

  // Remove a token from the active tokens list
  removeToken(token: CancelTokenSource): void {
    this.activeTokens = this.activeTokens.filter(t => t !== token);
  }

  // Get the current search API
  getCurrentSearchApi(): string {
    return BUSINESS_SEARCH_API;
  }

  // Set the search API and dispatch an event
  setSearchApi(api: string): void {
    if (api !== BUSINESS_SEARCH_API) {
      BUSINESS_SEARCH_API = api;
      window.dispatchEvent(searchApiChangedEvent);
    }
  }

  // Get the current contact search API
  getCurrentContactApi(): string {
    return CONTACT_SEARCH_API;
  }

  // Set the contact search API and dispatch an event
  setContactApi(api: string): void {
    if (api !== CONTACT_SEARCH_API) {
      CONTACT_SEARCH_API = api;
      window.dispatchEvent(contactApiChangedEvent);
    }
  }

  // Check if error is an OpenAI quota exceeded error
  private isOpenAIQuotaExceededError(error: unknown): boolean {
    if (!error || typeof error !== 'object' || error === null) {
      return false;
    }
    
    // Now we know error is a non-null object
    const errorObj = error as Record<string, unknown>;
    
    if (!('response' in errorObj) || !errorObj.response || typeof errorObj.response !== 'object' || errorObj.response === null) {
      return false;
    }
    
    // Now we know response is a non-null object
    const response = errorObj.response as Record<string, unknown>;
    
    if (!('data' in response) || !response.data || typeof response.data !== 'object' || response.data === null) {
      return false;
    }
    
    // Now we know data is a non-null object
    const data = response.data as Record<string, unknown>;
    
    if (!('error' in data)) {
      return false;
    }
    
    const errorValue = data.error;
    
    if (typeof errorValue === 'string') {
      return errorValue.includes('insufficient_quota') || errorValue.includes('exceeded your current quota');
    }
    
    if (typeof errorValue === 'object' && errorValue !== null) {
      const errorDetails = errorValue as Record<string, unknown>;
      const code = errorDetails.code;
      const message = errorDetails.message;
      
      return (
        (typeof code === 'string' && code === 'insufficient_quota') || 
        (typeof message === 'string' && message.includes('exceeded your current quota'))
      );
    }
    
    return false;
  }

  async findBusinessesInArea(data: LeadFormData, conversationId?: string, cancelToken?: CancelTokenSource): Promise<BusinessesResponse> {
    const token = cancelToken || this.createCancelToken();
    try {
      // Log the inputs for debugging
      console.log('findBusinessesInArea called with:', { 
        data: { ...data, instructions: data.instructions?.substring(0, 50) + '...' }, 
        conversationId, 
        hasToken: !!cancelToken 
      });
      
      // Add id to the request if provided
      const requestData = {
        ...data,
        ...(conversationId && { id: conversationId })
      };
      
      const endpoint = '/find-businesses-search';
      
      try {
        const response = await api.post(endpoint, requestData, {
          cancelToken: token.token
        });
        
        // Log the response for debugging
        console.log('API response received:', { 
          endpoint,
          hasBusinesses: !!(response.data.businesses && response.data.businesses.length),
          businessCount: response.data.businesses?.length || 0,
          hasId: !!response.data.id,
          id: response.data.id,
          hasMessage: !!response.data.message,
          noMoreResults: !!response.data.noMoreResults
        });
        
        // Check if we have a message and suggestions
        const businesses = response.data.businesses || [];
        const result = [...businesses] as BusinessesResponse;
        
        // Add message and suggestions properties if they exist
        if (response.data.message) {
          result.message = response.data.message;
        }
        
        if (response.data.suggestions) {
          result.suggestions = response.data.suggestions;
        }
        
        if (response.data.error) {
          result.error = response.data.error;
        }
        
        // Add id if it exists
        if (response.data.id) {
          result.id = response.data.id;
        } else if (conversationId) {
          // If the API didn't return an ID but we had one, preserve it
          result.id = conversationId;
        }
        
        // Add noMoreResults flag if it exists
        if (response.data.noMoreResults) {
          result.noMoreResults = response.data.noMoreResults;
        }
        
        return result;
      } catch (error) {
        // If we're using AI and get a quota exceeded error, fall back to Google Maps
        if (BUSINESS_SEARCH_API === 'ai' && this.isOpenAIQuotaExceededError(error)) {
          console.warn('OpenAI quota exceeded, falling back to Google Maps API');
          
          // Switch to Google Maps API for future requests
          this.setSearchApi('search');
          
          // Retry with Google Maps API
          const fallbackResponse = await api.post('/find-businesses-search', requestData, {
            cancelToken: token.token
          });
          
          // Process the response the same way
          const businesses = fallbackResponse.data.businesses || [];
          const result = [...businesses] as BusinessesResponse;
          
          if (fallbackResponse.data.message) {
            result.message = fallbackResponse.data.message;
          }
          
          if (fallbackResponse.data.suggestions) {
            result.suggestions = fallbackResponse.data.suggestions;
          }
          
          if (fallbackResponse.data.error) {
            result.error = fallbackResponse.data.error;
          }
          
          if (fallbackResponse.data.id) {
            result.id = fallbackResponse.data.id;
          }
          
          if (fallbackResponse.data.noMoreResults) {
            result.noMoreResults = fallbackResponse.data.noMoreResults;
          }
          
          // Add a message about the fallback
          result.message = `${result.message || ''} (Switched to Google Maps API due to OpenAI quota limits)`.trim();
          
          return result;
        }
        
        // If it's not a quota error or we're already using Google Maps, rethrow
        throw error;
      }
    } catch (error) {
      // Don't log cancellation errors as they're expected
      if (axios.isCancel(error)) {
        console.log('Request cancelled:', error.message);
      } else {
        console.error('Error finding businesses:', error);
      }
      throw error;
    } finally {
      // Remove the token if we created it
      if (!cancelToken) {
        this.removeToken(token);
      }
    }
  }

  async findContacts(website: string, cancelToken?: CancelTokenSource, instructions?: string, socialLinks?: string[]): Promise<ContactsResponse> {
    const token = cancelToken || this.createCancelToken();
    try {
      // Log the inputs for debugging
      console.log('findContacts called with:', { 
        website, 
        hasInstructions: !!instructions,
        hasSocialLinks: !!socialLinks,
        socialLinksCount: socialLinks?.length || 0,
        hasToken: !!cancelToken 
      });

      // Determine which endpoint to use based on the environment variable
      const endpoint = CONTACT_SEARCH_API === 'ai' ? '/find-contacts-ai' : '/find-contacts-crawl';
      
      // Make initial request to start the search
      const response = await api.post(endpoint, { 
        website,
        ...(instructions && { instructions }),
        ...(socialLinks && socialLinks.length > 0 && { socialLinks })
      }, {
        cancelToken: token.token
      });

      // If using AI endpoint, handle polling
      if (CONTACT_SEARCH_API === 'ai') {
        const { request_id } = response.data;
        
        // Function to poll for results
        const pollForResults = async (): Promise<ContactsResponse> => {
          const pollResponse = await api.get(`/poll-request/${request_id}`, {
            cancelToken: token.token
          });
          
          const { status, result, error } = pollResponse.data;
          
          if (status === 'error') {
            throw new Error(error || 'An error occurred while processing the request');
          }
          
          if (status === 'completed') {
            // Transform the result into the expected format
            const contacts = result.contacts || [];
            const contactsResponse = [...contacts] as ContactsResponse;
            
            // Add additional properties
            contactsResponse.description = result.description;
            contactsResponse.email = result.email;
            contactsResponse.socialLinks = result.socialLinks;
            contactsResponse.relevancyScore = result.relevancyScore;
            contactsResponse.id = result.id;
            
            return contactsResponse;
          }
          
          // If still processing, wait 2 seconds and try again
          await new Promise(resolve => setTimeout(resolve, 2000));
          return pollForResults();
        };
        
        // Start polling
        return await pollForResults();
      }
      
      // For non-AI endpoints, process response as before
      const contacts = response.data.contacts || [];
      const result = [...contacts] as ContactsResponse;
      
      // Add additional properties if they exist
      if (response.data.description) result.description = response.data.description;
      if (response.data.email) result.email = response.data.email;
      if (response.data.socialLinks) result.socialLinks = response.data.socialLinks;
      if (response.data.relevancyScore) result.relevancyScore = response.data.relevancyScore;
      if (response.data.id) result.id = response.data.id;
      
      return result;
    } catch (error) {
      // Handle OpenAI quota errors
      if (this.isOpenAIQuotaExceededError(error)) {
        console.log('OpenAI quota exceeded, switching to crawl API');
        this.setContactApi('crawl');
        // Retry with crawl API
        return this.findContacts(website, token, instructions, socialLinks);
      }
      
      // Remove the token from active tokens
      if (!cancelToken) {
        this.removeToken(token);
      }
      
      // Re-throw the error
      throw error;
    }
  }

  async reloadBusiness(business: BusinessData, cancelToken?: CancelTokenSource): Promise<BusinessInfoResponse> {
    const token = cancelToken || this.createCancelToken();
    try {
      const response = await api.post('/find-business', business, {
        cancelToken: token.token
      });
      
      // Create the result object
      const result = {
        business: response.data.business || {},
        contacts: response.data.contacts || [],
        summary: response.data.summary || ''
      } as BusinessInfoResponse;
      
      // Add message and suggestions properties if they exist
      if (response.data.message) {
        result.message = response.data.message;
      }
      
      if (response.data.suggestions) {
        result.suggestions = response.data.suggestions;
      }
      
      if (response.data.error) {
        result.error = response.data.error;
      }
      
      return result;
    } catch (error) {
      // Don't log cancellation errors as they're expected
      if (axios.isCancel(error)) {
        console.log('Request cancelled:', error.message);
      } else {
        console.error('Error reloading business:', error);
      }
      throw error;
    } finally {
      // Remove the token if we created it
      if (!cancelToken) {
        this.removeToken(token);
      }
    }
  }

  async supplementProfile(existingProfile: SupplementProfileRequest, socialLinks: string[], cancelToken?: CancelTokenSource, instructions?: string): Promise<SupplementProfileResponse> {
    const token = cancelToken || this.createCancelToken();
    try {
      // Log the inputs for debugging
      console.log('supplementProfile called with:', { 
        hasExistingProfile: !!existingProfile,
        socialLinksCount: socialLinks.length,
        hasInstructions: !!instructions,
        hasToken: !!cancelToken 
      });

      const response = await api.post('/supplement-profile', { 
        existingProfile,
        socialLinks,
        ...(instructions && { instructions })
      }, {
        cancelToken: token.token
      });

      return response.data;
    } catch (error) {
      // Don't log cancellation errors as they're expected
      if (axios.isCancel(error)) {
        console.log('Request cancelled:', error.message);
      } else {
        console.error('Error supplementing profile:', error);
      }
      throw error;
    } finally {
      // Remove the token if we created it
      if (!cancelToken) {
        this.removeToken(token);
      }
    }
  }

  async checkHealth(): Promise<boolean> {
    try {
      const response = await api.get('/health');
      return response.data.status === 'ok';
    } catch (error) {
      console.error('Error checking API health:', error);
      return false;
    }
  }
}

export default new ApiService(); 